import React, { useState } from 'react';
import {
    Newspaper, Filter, TrendingUp, BookOpen, Zap, RefreshCw,
    ChevronRight, ExternalLink, X as XIcon, Clock, Eye,
    ThumbsUp, Sparkles, Globe,
    MessageCircle, ArrowRight,
    SlidersHorizontal, Plug,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================
interface DigestItem {
    id: string;
    source: 'twitter' | 'linkedin' | 'instagram' | 'reddit' | 'rss';
    title: string;
    summary: string;
    relevanceScore: number;
    sentiment: 'positive' | 'neutral' | 'negative';
    category: string;
    url: string;
    timestamp: string;
    imageUrl?: string;
    engagement?: { likes?: number; comments?: number; shares?: number };
}

interface ConnectedSource {
    id: string;
    name: string;
    icon: React.ReactNode;
    connected: boolean;
    itemsFiltered: number;
    itemsSurfaced: number;
    color: string;
}

// ============================================================================
// Constants
// ============================================================================
const SOURCE_ICONS: Record<string, { svg: React.ReactNode; color: string; bg: string }> = {
    twitter: {
        color: '#1DA1F2',
        bg: 'bg-[#1DA1F2]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>,
    },
    linkedin: {
        color: '#0A66C2',
        bg: 'bg-[#0A66C2]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>,
    },
    instagram: {
        color: '#E4405F',
        bg: 'bg-[#E4405F]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#E4405F"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>,
    },
    reddit: {
        color: '#FF4500',
        bg: 'bg-[#FF4500]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#FF4500"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" /></svg>,
    },
    rss: {
        color: '#F26522',
        bg: 'bg-[#F26522]/10',
        svg: <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#F26522"><circle cx="6.18" cy="17.82" r="2.18" /><path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z" /></svg>,
    },
};

const SENTIMENT_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    positive: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Positive' },
    neutral: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Neutral' },
    negative: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Negative' },
};

// ============================================================================
// Mock Data
// ============================================================================
const MOCK_SOURCES: ConnectedSource[] = [
    { id: 'twitter', name: 'Twitter / X', icon: SOURCE_ICONS.twitter.svg, connected: true, itemsFiltered: 312, itemsSurfaced: 4, color: SOURCE_ICONS.twitter.color },
    { id: 'linkedin', name: 'LinkedIn', icon: SOURCE_ICONS.linkedin.svg, connected: true, itemsFiltered: 189, itemsSurfaced: 3, color: SOURCE_ICONS.linkedin.color },
    { id: 'reddit', name: 'Reddit', icon: SOURCE_ICONS.reddit.svg, connected: true, itemsFiltered: 247, itemsSurfaced: 3, color: SOURCE_ICONS.reddit.color },
    { id: 'rss', name: 'RSS Feeds', icon: SOURCE_ICONS.rss.svg, connected: true, itemsFiltered: 99, itemsSurfaced: 2, color: SOURCE_ICONS.rss.color },
    { id: 'instagram', name: 'Instagram', icon: SOURCE_ICONS.instagram.svg, connected: false, itemsFiltered: 0, itemsSurfaced: 0, color: SOURCE_ICONS.instagram.color },
];

const MOCK_DIGEST: DigestItem[] = [
    {
        id: '1', source: 'twitter', title: 'EU AI Act — New Compliance Guidelines Published',
        summary: 'The European Commission released updated compliance guidelines for the EU AI Act. Key changes include stricter transparency requirements for foundation models and new obligations for high-risk AI systems deployers.',
        relevanceScore: 94, sentiment: 'neutral', category: 'AI Regulation', url: '#',
        timestamp: '2h ago', engagement: { likes: 2340, comments: 187, shares: 892 },
    },
    {
        id: '2', source: 'linkedin', title: 'Your Connection Posted About Digital Sovereignty',
        summary: 'A senior contact shared insights on how personal data ownership is reshaping the SaaS landscape. They advocate for user-controlled AI agents — directly relevant to your work.',
        relevanceScore: 88, sentiment: 'positive', category: 'Industry Trends', url: '#',
        timestamp: '3h ago', engagement: { likes: 156, comments: 42 },
    },
    {
        id: '3', source: 'reddit', title: 'r\/artificial — Self-hosted AI Agents Gaining Traction',
        summary: 'Discussion thread (1.2k upvotes) about the shift from cloud-hosted to sovereign AI agents. Multiple users report switching from centralized LLM APIs to local-first models.',
        relevanceScore: 82, sentiment: 'positive', category: 'AI Development', url: '#',
        timestamp: '5h ago', engagement: { likes: 1247, comments: 389 },
    },
    {
        id: '4', source: 'rss', title: 'TechCrunch: The Future of Cognitive Augmentation',
        summary: 'Long-form article exploring how AI assistants can reduce information overload by pre-filtering and synthesizing content. Cites research showing 47% reduction in decision fatigue.',
        relevanceScore: 79, sentiment: 'positive', category: 'Technology', url: '#',
        timestamp: '6h ago',
    },
    {
        id: '5', source: 'twitter', title: 'Data Privacy Debate Heats Up in US Congress',
        summary: 'Multiple senators pushed back on proposed AI regulation, citing innovation concerns. The bill that would mandate data portability for AI agents is now in committee review.',
        relevanceScore: 71, sentiment: 'negative', category: 'Policy', url: '#',
        timestamp: '8h ago', engagement: { likes: 5600, comments: 1200, shares: 3400 },
    },
    {
        id: '6', source: 'linkedin', title: 'Job Alert: Head of AI Governance — Fortune 500',
        summary: 'A major tech company is hiring for a Head of AI Governance role. The description emphasizes accountability frameworks, agent oversight protocols, and human-in-the-loop systems.',
        relevanceScore: 67, sentiment: 'neutral', category: 'Career', url: '#',
        timestamp: '10h ago',
    },
    {
        id: '7', source: 'reddit', title: 'r/startups — Is Agent-as-a-Service the Next Big Thing?',
        summary: 'Lively debate about whether the "Human Replica" model (AI agents owned by individuals, hired by companies) is viable. Top comment argues the economics differ from traditional SaaS.',
        relevanceScore: 91, sentiment: 'positive', category: 'Business', url: '#',
        timestamp: '12h ago', engagement: { likes: 867, comments: 234 },
    },
    {
        id: '8', source: 'rss', title: 'Nature: Neural Interfaces and Cognitive Enhancement',
        summary: 'Peer-reviewed study on using AI-driven cognitive assistants to improve knowledge workers\' productivity by filtering information streams. Results show 62% improvement in comprehension.',
        relevanceScore: 85, sentiment: 'positive', category: 'Research', url: '#',
        timestamp: '1d ago',
    },
];

// ============================================================================
// Sub-components
// ============================================================================
const CognitiveLoadMeter: React.FC<{ totalFiltered: number; totalSurfaced: number }> = ({ totalFiltered, totalSurfaced }) => {
    const ratio = totalSurfaced / (totalFiltered || 1);
    const reductionPct = Math.round((1 - ratio) * 100);

    return (
        <div className="card p-5 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/[0.03] via-blue-500/[0.02] to-transparent pointer-events-none" />
            <div className="flex items-center justify-between mb-4 relative">
                <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                        <Sparkles size={20} className="text-indigo-400" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-hig-text-primary">Cognitive Load Relief</h3>
                        <p className="text-xs text-hig-text-tertiary">Your Replica filtered out the noise</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-2xl font-bold text-hig-accent">{reductionPct}%</p>
                    <p className="text-[10px] text-hig-text-tertiary uppercase tracking-wider">Noise Reduced</p>
                </div>
            </div>

            {/* Visual pipeline */}
            <div className="flex items-center gap-3 relative">
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-hig-text-secondary">{totalFiltered.toLocaleString()} items scanned</span>
                        <span className="text-[11px] font-bold text-hig-accent">{totalSurfaced} highlights</span>
                    </div>
                    <div className="relative w-full h-3 bg-hig-bg-tertiary rounded-full overflow-hidden">
                        {/* Full bar (scanned) */}
                        <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 via-amber-500/20 to-red-500/20 rounded-full cognitive-load-shimmer" />
                        {/* Surfaced portion */}
                        <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-hig-accent to-indigo-400 rounded-full transition-all duration-1000 ease-out shadow-sm shadow-hig-accent/30"
                            style={{ width: `${Math.max(ratio * 100, 2)}%` }}
                        />
                    </div>
                </div>
                <ArrowRight size={16} className="text-hig-accent flex-shrink-0 animate-pulse" />
                <div className="w-12 h-12 rounded-xl bg-hig-accent/10 border border-hig-accent/20 flex items-center justify-center flex-shrink-0">
                    <Eye size={18} className="text-hig-accent" />
                </div>
            </div>
        </div>
    );
};

const DigestCard: React.FC<{
    item: DigestItem;
    onDismiss: (id: string) => void;
}> = ({ item, onDismiss }) => {
    const sourceStyle = SOURCE_ICONS[item.source];
    const sentimentStyle = SENTIMENT_STYLES[item.sentiment];

    return (
        <div className="card p-4 hover:bg-hig-bg-elevated/50 transition-all group relative overflow-hidden">
            {/* Relevance indicator bar */}
            <div
                className="absolute top-0 left-0 h-0.5 bg-gradient-to-r from-hig-accent to-indigo-400 transition-all duration-500"
                style={{ width: `${item.relevanceScore}%` }}
            />

            <div className="flex items-start gap-3">
                {/* Source icon */}
                <div className={`w-9 h-9 rounded-lg ${sourceStyle.bg} border border-hig-border flex items-center justify-center flex-shrink-0 mt-0.5`}
                    style={{ color: sourceStyle.color }}>
                    {sourceStyle.svg}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h4 className="text-sm font-semibold text-hig-text-primary leading-tight line-clamp-2 group-hover:text-hig-accent transition-colors">
                            {item.title}
                        </h4>
                        <button
                            onClick={() => onDismiss(item.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-hig-text-tertiary hover:text-hig-text-secondary p-1 -mt-1 -mr-1 flex-shrink-0"
                            title="Dismiss"
                        >
                            <XIcon size={14} />
                        </button>
                    </div>

                    <p className="text-xs text-hig-text-secondary leading-relaxed mb-3 line-clamp-3">
                        {item.summary}
                    </p>

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Category */}
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-hig-bg-tertiary text-hig-text-secondary font-medium">
                                {item.category}
                            </span>
                            {/* Sentiment */}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${sentimentStyle.bg} ${sentimentStyle.text} font-medium`}>
                                {sentimentStyle.label}
                            </span>
                            {/* Relevance */}
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                item.relevanceScore >= 85 ? 'bg-hig-accent/10 text-hig-accent' :
                                item.relevanceScore >= 70 ? 'bg-amber-500/10 text-amber-400' :
                                'bg-hig-bg-tertiary text-hig-text-tertiary'
                            }`}>
                                {item.relevanceScore}% relevant
                            </span>
                            {/* Time */}
                            <span className="text-[10px] text-hig-text-tertiary flex items-center gap-1">
                                <Clock size={10} /> {item.timestamp}
                            </span>
                        </div>

                        <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[10px] text-hig-accent hover:text-hig-accent/80 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            Read <ExternalLink size={10} />
                        </a>
                    </div>

                    {/* Engagement stats */}
                    {item.engagement && (
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-hig-divider/50">
                            {item.engagement.likes != null && (
                                <span className="text-[10px] text-hig-text-tertiary flex items-center gap-1">
                                    <ThumbsUp size={10} /> {item.engagement.likes.toLocaleString()}
                                </span>
                            )}
                            {item.engagement.comments != null && (
                                <span className="text-[10px] text-hig-text-tertiary flex items-center gap-1">
                                    <MessageCircle size={10} /> {item.engagement.comments.toLocaleString()}
                                </span>
                            )}
                            {item.engagement.shares != null && (
                                <span className="text-[10px] text-hig-text-tertiary flex items-center gap-1">
                                    <RefreshCw size={10} /> {item.engagement.shares.toLocaleString()}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================
interface SocialSynthesisViewProps {
    onNavigate: (view: string) => void;
}

export default function SocialSynthesisView({ onNavigate }: SocialSynthesisViewProps) {
    const [activeTab, setActiveTab] = useState<'digest' | 'news' | 'social' | 'deep-reads'>('digest');
    const [items, setItems] = useState<DigestItem[]>(MOCK_DIGEST);
    const [sources] = useState<ConnectedSource[]>(MOCK_SOURCES);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedSource, setSelectedSource] = useState<string | null>(null);
    const [urgencyThreshold, setUrgencyThreshold] = useState(50);
    const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month'>('today');
    const [syncing, setSyncing] = useState(false);

    const connectedSources = sources.filter(s => s.connected);
    const disconnectedSources = sources.filter(s => !s.connected);
    const totalFiltered = connectedSources.reduce((sum, s) => sum + s.itemsFiltered, 0);
    const totalSurfaced = connectedSources.reduce((sum, s) => sum + s.itemsSurfaced, 0);

    // Filter items
    const filteredItems = items.filter(item => {
        if (selectedSource && item.source !== selectedSource) return false;
        if (item.relevanceScore < urgencyThreshold) return false;
        return true;
    });

    // Tab-specific filtering
    const tabItems = filteredItems.filter(item => {
        switch (activeTab) {
            case 'news': return ['AI Regulation', 'Policy', 'Technology', 'Research'].includes(item.category);
            case 'social': return ['Industry Trends', 'Career', 'Business'].includes(item.category);
            case 'deep-reads': return item.relevanceScore >= 80;
            default: return true;
        }
    });

    const handleDismiss = (id: string) => {
        setItems(prev => prev.filter(i => i.id !== id));
    };

    const handleSync = () => {
        setSyncing(true);
        setTimeout(() => setSyncing(false), 2000);
    };

    // Empty state — no sources connected
    if (connectedSources.length === 0) {
        return (
            <div className="max-w-3xl mx-auto animate-fade-in">
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/10">
                        <Newspaper size={36} className="text-indigo-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-hig-text-primary mb-3">Feed Synthesis</h2>
                    <p className="text-sm text-hig-text-secondary mb-2 max-w-md leading-relaxed">
                        Let your Replica read your social media feeds and news sources so you don't have to.
                        It filters noise and surfaces only what matters.
                    </p>
                    <p className="text-xs text-hig-text-tertiary mb-8 max-w-sm">
                        Connect your social accounts to get AI-synthesized digests that relieve cognitive overload.
                    </p>
                    <button
                        onClick={() => onNavigate('integrations')}
                        className="btn-primary px-8 py-3 rounded-xl font-semibold text-sm flex items-center gap-2 shadow-lg shadow-hig-accent/25"
                    >
                        <Plug size={16} />
                        Connect Social Accounts
                    </button>
                    <div className="mt-10 grid grid-cols-5 gap-4">
                        {sources.map(s => (
                            <div key={s.id} className="flex flex-col items-center gap-2 opacity-40">
                                <div className={`w-12 h-12 rounded-xl ${SOURCE_ICONS[s.id].bg} border border-hig-border flex items-center justify-center`}
                                    style={{ color: SOURCE_ICONS[s.id].color }}>
                                    {SOURCE_ICONS[s.id].svg}
                                </div>
                                <span className="text-[10px] text-hig-text-tertiary">{s.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto animate-fade-in pb-12">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-xl font-bold text-hig-text-primary flex items-center gap-2">
                        <Sparkles size={20} className="text-hig-accent" />
                        Feed Synthesis
                    </h1>
                    <p className="text-xs text-hig-text-secondary mt-1">
                        Your Replica reads the internet so you don't have to
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-hig-text-tertiary">Last synced: 14 min ago</span>
                    <button
                        onClick={handleSync}
                        disabled={syncing}
                        className="p-2 rounded-lg hover:bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary transition-colors disabled:opacity-50"
                        title="Sync now"
                    >
                        <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Connected Sources Bar */}
            <div className="flex items-center gap-3 mb-5">
                <div className="flex items-center gap-1.5">
                    {connectedSources.map(s => (
                        <button
                            key={s.id}
                            onClick={() => setSelectedSource(selectedSource === s.id ? null : s.id)}
                            className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${
                                selectedSource === s.id
                                    ? 'border-hig-accent/40 bg-hig-accent/10 ring-1 ring-hig-accent/20'
                                    : 'border-hig-border bg-hig-bg-tertiary hover:bg-hig-bg-elevated'
                            }`}
                            style={{ color: s.color }}
                            title={`${s.name}: ${s.itemsSurfaced} highlights from ${s.itemsFiltered} items`}
                        >
                            {s.icon}
                        </button>
                    ))}
                </div>
                {selectedSource && (
                    <button
                        onClick={() => setSelectedSource(null)}
                        className="text-[10px] text-hig-accent hover:underline"
                    >
                        Clear filter
                    </button>
                )}
                <div className="flex-1" />
                {disconnectedSources.length > 0 && (
                    <button
                        onClick={() => onNavigate('integrations')}
                        className="flex items-center gap-1.5 text-[11px] text-hig-text-tertiary hover:text-hig-accent transition-colors"
                    >
                        <Plug size={12} />
                        Connect {disconnectedSources.length} more
                        <ChevronRight size={12} />
                    </button>
                )}
            </div>

            {/* Cognitive Load Meter */}
            <div className="mb-5">
                <CognitiveLoadMeter totalFiltered={totalFiltered} totalSurfaced={totalSurfaced} />
            </div>

            {/* Tabs + Filter Toggle */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex border-b border-hig-divider">
                    {[
                        { key: 'digest', label: 'Daily Digest', icon: Newspaper },
                        { key: 'news', label: 'News Filter', icon: Globe },
                        { key: 'social', label: 'Social Pulse', icon: TrendingUp },
                        { key: 'deep-reads', label: 'Deep Reads', icon: BookOpen },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key as any)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                                activeTab === tab.key
                                    ? 'border-hig-accent text-hig-text-primary'
                                    : 'border-transparent text-hig-text-secondary hover:text-hig-text-primary'
                            }`}
                        >
                            <tab.icon size={14} />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        showFilters ? 'bg-hig-accent/10 text-hig-accent' : 'text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                    }`}
                >
                    <SlidersHorizontal size={14} />
                    Filters
                </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <div className="card p-4 mb-4 animate-fade-in">
                    <div className="grid grid-cols-3 gap-4">
                        {/* Time Range */}
                        <div>
                            <label className="text-[10px] text-hig-text-tertiary uppercase tracking-wider font-medium mb-2 block">Time Range</label>
                            <div className="flex gap-1">
                                {(['today', 'week', 'month'] as const).map(range => (
                                    <button
                                        key={range}
                                        onClick={() => setTimeRange(range)}
                                        className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors capitalize ${
                                            timeRange === range
                                                ? 'bg-hig-accent text-[#0F172A]'
                                                : 'bg-hig-bg-tertiary text-hig-text-secondary hover:text-hig-text-primary'
                                        }`}
                                    >
                                        {range}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* Urgency Threshold */}
                        <div>
                            <label className="text-[10px] text-hig-text-tertiary uppercase tracking-wider font-medium mb-2 block">
                                Min. Relevance: {urgencyThreshold}%
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={100}
                                value={urgencyThreshold}
                                onChange={e => setUrgencyThreshold(Number(e.target.value))}
                                className="w-full accent-hig-accent"
                            />
                        </div>
                        {/* Source filter */}
                        <div>
                            <label className="text-[10px] text-hig-text-tertiary uppercase tracking-wider font-medium mb-2 block">Source</label>
                            <select
                                value={selectedSource || ''}
                                onChange={e => setSelectedSource(e.target.value || null)}
                                className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-3 py-1.5 text-xs text-hig-text-primary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                            >
                                <option value="">All Sources</option>
                                {connectedSources.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Digest List */}
            <div className="space-y-3">
                {tabItems.length > 0 ? (
                    tabItems.map(item => (
                        <DigestCard key={item.id} item={item} onDismiss={handleDismiss} />
                    ))
                ) : (
                    <div className="text-center py-16 text-hig-text-tertiary">
                        <Filter size={32} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No items match your filters</p>
                        <p className="text-xs mt-1">Try lowering the relevance threshold or changing the source filter.</p>
                    </div>
                )}
            </div>

            {/* Tab-specific info footer */}
            {activeTab === 'digest' && tabItems.length > 0 && (
                <div className="mt-6 card p-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                        <Zap size={16} className="text-indigo-400" />
                    </div>
                    <div className="flex-1">
                        <p className="text-xs text-hig-text-secondary">
                            <strong className="text-hig-text-primary">Your Replica synthesized {totalFiltered.toLocaleString()} items</strong> from {connectedSources.length} sources into {totalSurfaced} actionable highlights. Estimated time saved: <strong className="text-hig-accent">~45 min</strong>.
                        </p>
                    </div>
                </div>
            )}

            {activeTab === 'deep-reads' && tabItems.length > 0 && (
                <div className="mt-6 card p-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen size={16} className="text-purple-400" />
                    </div>
                    <div className="flex-1">
                        <p className="text-xs text-hig-text-secondary">
                            <strong className="text-hig-text-primary">Deep Reads</strong> are articles your Replica flagged as worth reading in full based on your interests and cognitive profile.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
